import { V1_AREA_COLORS } from './marketingDemoData';

const STEP_WEIGHTS = [0.3, 0.2, 0.1, -0.1, -0.2, -0.3, 0];

/** Production V1 Fs order + colors (SelectGoalsStep2 initial state). Scores are 0–10. */
export const demoStepFs = [
    {
        areaId: 1,
        name: 'Fitness',
        color: '#15C285',
        score: 6.2,
        recommendedGoals: [],
    },
    {
        areaId: 2,
        name: 'Finance',
        color: '#ABD86D',
        score: 5.1,
        recommendedGoals: [],
    },
    {
        areaId: 3,
        name: 'Family',
        color: '#FBBF53',
        score: 7.1,
        recommendedGoals: [],
    },
    {
        areaId: 4,
        name: 'Faith',
        color: '#F26636',
        score: 5.8,
        recommendedGoals: [],
    },
    {
        areaId: 5,
        name: 'Field',
        color: '#EC0F47',
        score: 5.5,
        recommendedGoals: [],
    },
    {
        areaId: 6,
        name: 'Friends',
        color: '#700460',
        score: 4.4,
        recommendedGoals: [],
    },
    {
        areaId: 7,
        name: 'Fun',
        color: '#022B7A',
        score: 5.3,
        recommendedGoals: [],
    },
];

const FITNESS_QUESTION_TEXTS = [
    {
        title: 'How often do you exercise each week?',
        text: 'Include any intentional physical activity — walking, gym, sports, or active hobbies.',
    },
    {
        title: 'How would you rate your overall energy level?',
        text: 'Think about how you feel throughout a typical day, not just after coffee.',
    },
    {
        title: 'How balanced do you feel in this area of your life?',
        text: 'Consider your overall health, energy, and habits. Answer honestly based on where you are today.',
    },
    {
        title: 'How consistent are your sleep habits?',
        text: 'Include bedtime routine, hours of sleep, and how rested you feel in the morning.',
    },
    {
        title: 'How well do you manage stress physically?',
        text: 'Consider tension, recovery, and how your body responds to daily demands.',
    },
    {
        title: 'How nutritious is your typical diet?',
        text: 'Think about whole foods, hydration, and how often you eat mindfully.',
    },
    {
        title: 'How active are you outside of structured exercise?',
        text: 'Walking, standing, playing with kids, gardening, and daily movement all count.',
    },
    {
        title: 'How confident are you in your physical health long-term?',
        text: 'Consider habits you maintain and changes you could realistically sustain.',
    },
    {
        title: 'How well do you recover after physical exertion?',
        text: 'Include rest days, stretching, and how quickly you bounce back.',
    },
    {
        title: 'How satisfied are you with your fitness progress?',
        text: 'Reflect on where you started and where you want to be in the next 90 days.',
    },
];

const AREA_GOAL_OPTIONS = [
    [
        'Exercise 4 days per week',
        'Improve sleep to 7+ hours nightly',
        'Meal prep healthy lunches on Sundays',
    ],
    [
        'Build a 3-month emergency fund',
        'Track every expense for 30 days',
        'Automate 10% to savings each paycheck',
    ],
    [
        'Weekly family dinner — no phones',
        'Plan a monthly family outing',
        'Write a letter to each child monthly',
    ],
    [
        'Morning reflection 10 minutes daily',
        'Attend weekly service or study group',
        'Volunteer monthly in the community',
    ],
    [
        'Complete professional certification',
        'Network with 2 mentors quarterly',
        'Update resume and LinkedIn profile',
    ],
    [
        'Reconnect with two close friends',
        'Host a dinner party for friends',
        'Schedule monthly coffee dates',
    ],
    [
        'Plan one fun adventure per month',
        'Try a new hobby this quarter',
        'Take a weekend trip without work',
    ],
];

function buildFitnessQuestions(currentQuestionIndex, currentScore) {
    return FITNESS_QUESTION_TEXTS.map((q, i) => ({
        id: i + 1,
        title: q.title,
        text: q.text,
        score:
            i < currentQuestionIndex
                ? 6
                : i === currentQuestionIndex
                  ? currentScore
                  : null,
    }));
}

/** Step 1 state — Fitness Q3, matches production Step12 + Fnav layout. */
export function buildDemoStep1State() {
    const currentQuestion = 2;
    const currentScore = 7;

    const Fs = demoStepFs.map((f, idx) => ({
        id: f.areaId,
        name: f.name,
        color: f.color,
        weight: STEP_WEIGHTS[idx],
        questions:
            idx === 0
                ? buildFitnessQuestions(currentQuestion, currentScore)
                : Array.from({ length: 10 }, (_, i) => ({
                      id: i + 1,
                      title: '',
                      text: '',
                      score: null,
                  })),
        TotalScore:
            idx === 0
                ? buildFitnessQuestions(currentQuestion, currentScore).reduce(
                      (sum, q) => sum + (q.score || 0),
                      0
                  )
                : 0,
    }));

    return {
        index: 0,
        currentQuestion,
        lastF: 1,
        confetti: 0,
        hideNav: false,
        Fs,
        User: { Name: 'Jordan' },
        changeStepIndex: () => {},
    };
}

/** Minimal AllState for production Step 2 Wheel (Pick 7 / allColors). */
export function buildDemoStep2WheelState() {
    return {
        Fs: demoStepFs,
        CurrentF: 0,
        latestAssessmentScore: 5.5,
    };
}

/** Step 2 Pick 7 state — 21 goals in 7 area groups, 7 selected. */
export function buildDemoStep2State() {
    const SelectedGoalsGroup = {};
    const SelectedGoalsSeven = [];
    const SelectedGoals = [];

    demoStepFs.forEach((f, areaIndex) => {
        const completeDate = new Date('2026-09-15');
        completeDate.setDate(completeDate.getDate() + areaIndex * 7);

        const selected = AREA_GOAL_OPTIONS[areaIndex].map((text, goalIndex) => {
            const isSelected = goalIndex === 0;
            const idL = `${areaIndex}${goalIndex}`;
            const goal = {
                areaId: areaIndex,
                recomendationIndex: goalIndex,
                text,
                date: completeDate,
                Id: areaIndex * 10 + goalIndex,
                GoalId: areaIndex * 10 + goalIndex,
                selected: isSelected,
                id: isSelected ? idL : undefined,
                idL: isSelected ? idL : undefined,
            };
            if (isSelected) {
                SelectedGoalsSeven.push(goal);
            }
            SelectedGoals.push(goal);
            return goal;
        });

        SelectedGoalsGroup[areaIndex] = {
            Fs: f,
            selected,
        };
    });

    return {
        CurrentF: 0,
        CurrentStep2: 3,
        LastReachedF: 6,
        latestAssessmentScore: 5.5,
        Fs: demoStepFs,
        SelectedGoals,
        SelectedGoalsSeven,
        SelectedGoalsGroup,
        SelectedOolaOne: {},
    };
}

export const demoStep3Goals = [
    {
        id: 1,
        text: 'Exercise 4 days per week',
        color: V1_AREA_COLORS.Fitness,
        completedByDate: '2026-08-15',
    },
    {
        id: 2,
        text: 'Build a 3-month emergency fund',
        color: V1_AREA_COLORS.Finance,
        completedByDate: '2026-09-30',
    },
    {
        id: 3,
        text: 'Weekly family dinner — no phones',
        color: V1_AREA_COLORS.Family,
        completedByDate: '2026-07-01',
    },
    {
        id: 4,
        text: 'Complete professional certification',
        color: V1_AREA_COLORS.Field,
        completedByDate: '2026-12-01',
    },
    {
        id: 5,
        text: 'Morning reflection 10 minutes daily',
        color: V1_AREA_COLORS.Faith,
        completedByDate: '2026-08-01',
    },
    {
        id: 6,
        text: 'Reconnect with two close friends',
        color: V1_AREA_COLORS.Friends,
        completedByDate: '2026-10-15',
    },
    {
        id: 7,
        text: 'Plan one fun adventure per month',
        color: V1_AREA_COLORS.Fun,
        completedByDate: '2026-11-01',
    },
];

export const demoStep3Actions = [
    { text: 'Morning gratitude journal (3 items)' },
    { text: '20-minute walk outside' },
    { text: 'Review weekly budget goals' },
];

/** Step 3 state for SelectedGoal7 / SelectedAction components. */
export function buildDemoStep3State() {
    return {
        My7: demoStep3Goals,
        Action: demoStep3Actions,
    };
}

/** My Path — production lesson catalog. */
export const demoMyPathModules = [
    {
        id: 34,
        title: 'You are Designed for Greatness',
        imagePath: 'media/elearning/images/LiveAbundantly.png',
        isUnlocked: true,
        isCompleted: true,
        unLockDateText: '',
    },
    {
        id: 38,
        title: 'Progress Over Perfection',
        imagePath: 'media/elearning/images/ProgressOverPerfection.png',
        isUnlocked: true,
        isCompleted: true,
        unLockDateText: '',
    },
    {
        id: 40,
        title: 'Fuel your Passion for Fitness',
        imagePath: 'media/elearning/images/FitnessHigh.png',
        isUnlocked: true,
        isCompleted: false,
        unLockDateText: '',
    },
    {
        id: 39,
        title: 'Momentum as Fuel',
        imagePath: 'media/elearning/images/MomentumAsFuel.png',
        isUnlocked: false,
        isCompleted: false,
        unLockDateText: 'Unlocks May 15',
    },
];

export const demoPurchasedCourse = {
    id: 1002,
    title: 'The Miracle Mindset',
    imageUrl: 'media/images/dashboard/Miracle_Mindset_course.jpg',
};

/** Modules inside a purchased Additional Program course. */
export const demoPurchasedCourseModules = [
    {
        id: 201,
        title: 'Welcome to Miracle Mindset',
        imagePath: 'media/images/dashboard/Miracle_Mindset_course.jpg',
        isUnlocked: true,
        isCompleted: true,
        unLockDateText: '',
    },
    {
        id: 202,
        title: 'Rewire Your Thoughts',
        imagePath: 'media/elearning/images/ProgressOverPerfection.png',
        isUnlocked: true,
        isCompleted: false,
        unLockDateText: '',
    },
    {
        id: 203,
        title: 'Daily Mindset Practices',
        imagePath: 'media/elearning/images/Daily_action.png',
        isUnlocked: true,
        isCompleted: false,
        unLockDateText: '',
    },
    {
        id: 204,
        title: 'Building Lasting Habits',
        imagePath: 'media/elearning/images/MomentumAsFuel.png',
        isUnlocked: false,
        isCompleted: false,
        unLockDateText: 'Unlocks Apr 28',
    },
];
